/**
 * CT Four-Step Routine — desktop scroll scrub + mobile swipe/arrows/dots
 */
import {
  getScrollTop,
  getScrollEventTarget,
  getLenis,
  scrollTo,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const ROOT = '[data-ct-fsr]';
const DESKTOP = window.matchMedia('(min-width: 990px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @param {number} a
 * @param {number} b
 * @param {number} t
 */
const lerp = (a, b, t) => a + (b - a) * t;

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * @param {number} t
 */
const smoothstep = (t) => {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
};

/**
 * @param {HTMLElement} root
 */
const init = (root) => {
  if (root.dataset.ctFsrReady === 'true') return;
  root.dataset.ctFsrReady = 'true';

  const track = /** @type {HTMLElement | null} */ (root.querySelector('[data-ct-fsr-track]'));
  const stage = /** @type {HTMLElement | null} */ (root.querySelector('[data-ct-fsr-stage]'));
  const stack = /** @type {HTMLElement | null} */ (root.querySelector('[data-ct-fsr-stack]'));
  const nav = /** @type {HTMLElement | null} */ (root.querySelector('[data-ct-fsr-nav]'));
  const prevBtn = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-ct-fsr-prev]'));
  const nextBtn = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-ct-fsr-next]'));
  const cards = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-ct-fsr-card]')]);
  const dots = /** @type {HTMLButtonElement[]} */ ([...root.querySelectorAll('[data-ct-fsr-dot]')]);

  if (!track || !stage || !stack || cards.length === 0) return;

  const count = cards.length;
  const peek = () => {
    const raw = getComputedStyle(root).getPropertyValue('--ct-fsr-peek').trim();
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? n : 18;
  };

  const stepScrollVh = () => {
    const raw = Number.parseFloat(root.dataset.stepScrollVh || '90');
    return Number.isFinite(raw) ? raw : 90;
  };

  /** @type {'desktop' | 'mobile'} */
  let mode = 'desktop';
  let activeIndex = 0;
  let targetProgress = 0;
  let renderProgress = 0;
  let ticking = false;
  /** @type {number | null} */
  let touchStartX = null;
  /** @type {number | null} */
  let touchStartY = null;

  const clearCardInline = (/** @type {HTMLElement} */ card) => {
    card.style.transform = '';
    card.style.opacity = '';
    card.style.zIndex = '';
    card.style.pointerEvents = '';
    card.style.height = '';
    card.style.top = '';
    card.style.left = '';
    card.style.right = '';
    card.style.bottom = '';
    card.style.removeProperty('--ct-fsr-content-opacity');
  };

  const setMode = () => {
    mode = DESKTOP.matches ? 'desktop' : 'mobile';
    root.dataset.mode = mode;
    if (nav) {
      // Visibility is CSS-driven via [data-mode]; keep aria in sync
      nav.setAttribute('aria-hidden', mode === 'mobile' ? 'false' : 'true');
    }
  };

  const setTrackHeight = () => {
    if (mode !== 'desktop') {
      track.style.height = '';
      return;
    }
    const vh = stepScrollVh();
    const stageH = stage.offsetHeight || window.innerHeight * 0.75;
    const scrub = ((count - 1) * vh * window.innerHeight) / 100;
    track.style.height = `${Math.max(stageH + scrub, stageH)}px`;
  };

  /**
   * @param {number} index
   * @param {boolean} [announce]
   */
  const syncChrome = (index, announce = false) => {
    cards.forEach((card, i) => {
      const on = i === index;
      card.classList.toggle('is-active', on);
      card.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    dots.forEach((dot, i) => {
      const on = i === index;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (prevBtn) {
      prevBtn.disabled = index <= 0;
      prevBtn.setAttribute('aria-disabled', index <= 0 ? 'true' : 'false');
    }
    if (nextBtn) {
      nextBtn.disabled = index >= count - 1;
      nextBtn.setAttribute('aria-disabled', index >= count - 1 ? 'true' : 'false');
    }
    if (announce && stack) {
      const label =
        cards[index]?.querySelector('.ct-fsr__step-title')?.textContent?.trim() || `Step ${index + 1}`;
      stack.setAttribute('aria-label', label);
    }
  };

  /**
   * Mobile: one card in normal flow. Desktop: scrubbed stack.
   * @param {number} progress
   */
  const applyStack = (progress) => {
    const p = clamp(progress, 0, count - 1);
    const rounded = Math.round(p);

    if (mode === 'mobile') {
      if (rounded !== activeIndex) activeIndex = rounded;
      cards.forEach((card, i) => {
        clearCardInline(card);
        card.classList.toggle('is-active', i === rounded);
        card.setAttribute('aria-hidden', i === rounded ? 'false' : 'true');
      });
      syncChrome(rounded);
      return;
    }

    if (rounded !== activeIndex) {
      activeIndex = rounded;
      syncChrome(activeIndex);
    }

    const peekPx = peek();

    cards.forEach((card, i) => {
      const delta = i - p;
      const abs = Math.abs(delta);

      let y = 0;
      let scale = 1;
      let opacity = 1;
      let z = count - i;
      let contentOpacity = 0;

      if (abs < 0.001) {
        y = 0;
        scale = 1;
        opacity = 1;
        contentOpacity = 1;
        z = count + 2;
      } else if (delta > 0) {
        const depth = Math.min(delta, 3);
        y = depth * peekPx;
        scale = 1 - depth * 0.014;
        opacity = 1;
        contentOpacity = clamp(1 - delta * 1.35, 0, 1);
        z = count - i;
      } else {
        const t = smoothstep(clamp(-delta, 0, 1.15));
        y = lerp(0, -36, t);
        scale = lerp(1, 0.935, t);
        opacity = lerp(1, 0, Math.min(1, -delta * 1.05));
        contentOpacity = clamp(1 + delta * 1.4, 0, 1);
        z = count + Math.round(delta);
      }

      card.style.setProperty('--ct-fsr-content-opacity', String(clamp(contentOpacity, 0, 1)));
      card.style.zIndex = String(Math.round(z + 10));
      card.style.opacity = String(clamp(opacity, 0, 1));
      card.style.transform = `translate3d(0, ${y}px, 0) scale(${scale})`;
      card.style.pointerEvents = abs < 0.4 ? 'auto' : 'none';
    });
  };

  const readDesktopProgress = () => {
    const rect = track.getBoundingClientRect();
    const trackTop = getScrollTop() + rect.top;
    const distance = Math.max(1, track.offsetHeight - stage.offsetHeight);
    const local = getScrollTop() - trackTop;
    const t = clamp(local / distance, 0, 1);
    return t * (count - 1);
  };

  const schedule = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      if (mode === 'desktop') {
        targetProgress = readDesktopProgress();
      } else {
        targetProgress = activeIndex;
      }

      const ease = REDUCED.matches ? 1 : mode === 'desktop' ? 0.18 : 0.28;
      renderProgress = lerp(renderProgress, targetProgress, ease);
      if (Math.abs(renderProgress - targetProgress) < 0.001) renderProgress = targetProgress;
      applyStack(renderProgress);

      if (Math.abs(renderProgress - targetProgress) > 0.001) schedule();
    });
  };

  /**
   * @param {number} index
   */
  const goTo = (index) => {
    const next = clamp(Math.round(index), 0, count - 1);
    activeIndex = next;
    targetProgress = next;
    syncChrome(next, true);

    if (mode === 'mobile') {
      renderProgress = next;
      applyStack(next);
      return;
    }

    const rect = track.getBoundingClientRect();
    const trackTop = getScrollTop() + rect.top;
    const distance = Math.max(1, track.offsetHeight - stage.offsetHeight);
    const top = trackTop + (next / Math.max(1, count - 1)) * distance;
    scrollTo({ top, behavior: REDUCED.matches ? 'instant' : 'smooth' });
  };

  const onScroll = () => {
    if (mode !== 'desktop') return;
    schedule();
  };

  const onResize = () => {
    const prev = mode;
    setMode();
    setTrackHeight();
    if (prev !== mode) {
      cards.forEach(clearCardInline);
      renderProgress = activeIndex;
      targetProgress = activeIndex;
      applyStack(activeIndex);
      syncChrome(activeIndex);
    } else if (mode === 'desktop') {
      schedule();
    } else {
      applyStack(activeIndex);
    }
  };

  const onTouchStart = (event) => {
    if (mode !== 'mobile' || event.touches.length !== 1) return;
    touchStartX = event.touches[0].clientX;
    touchStartY = event.touches[0].clientY;
  };

  const onTouchEnd = (event) => {
    if (mode !== 'mobile' || touchStartX == null || touchStartY == null) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - touchStartX;
    const dy = touch.clientY - touchStartY;
    touchStartX = null;
    touchStartY = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.1) return;
    if (dx < 0) goTo(activeIndex + 1);
    else goTo(activeIndex - 1);
  };

  prevBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    goTo(activeIndex - 1);
  });
  nextBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    goTo(activeIndex + 1);
  });
  dots.forEach((dot) => {
    dot.addEventListener('click', (event) => {
      event.preventDefault();
      const i = Number.parseInt(dot.dataset.index || '0', 10);
      goTo(i);
    });
  });

  stack.addEventListener(
    'keydown',
    (event) => {
      if (mode !== 'mobile') return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        goTo(activeIndex + 1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        goTo(activeIndex - 1);
      }
    },
    true
  );

  stack.tabIndex = 0;
  stage.addEventListener('touchstart', onTouchStart, { passive: true });
  stage.addEventListener('touchend', onTouchEnd, { passive: true });

  /** @type {(() => void) | null} */
  let detachScroll = null;

  const bindScroll = () => {
    detachScroll?.();
    const target = getScrollEventTarget();
    const handler = () => onScroll();
    target.addEventListener('scroll', handler, { passive: true });
    const lenis = getLenis();
    /** @type {((e: unknown) => void) | null} */
    let lenisHandler = null;
    if (lenis && typeof lenis.on === 'function') {
      lenisHandler = () => onScroll();
      lenis.on('scroll', lenisHandler);
    }
    detachScroll = () => {
      target.removeEventListener('scroll', handler);
      if (lenis && lenisHandler && typeof lenis.off === 'function') {
        lenis.off('scroll', lenisHandler);
      }
    };
  };

  setMode();
  setTrackHeight();
  syncChrome(0);
  applyStack(0);
  bindScroll();
  if (mode === 'desktop') schedule();

  DESKTOP.addEventListener('change', onResize);
  scrollContainerMediaQuery.addEventListener('change', () => {
    bindScroll();
    onResize();
  });
  window.addEventListener('resize', onResize, { passive: true });

  requestAnimationFrame(() => {
    setTrackHeight();
    if (mode === 'desktop') schedule();
    else applyStack(activeIndex);
  });
};

const boot = () => {
  document.querySelectorAll(ROOT).forEach((el) => init(/** @type {HTMLElement} */ (el)));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = /** @type {HTMLElement} */ (event.target);
  section.querySelectorAll?.(ROOT).forEach((el) => {
    /** @type {HTMLElement} */ (el).dataset.ctFsrReady = 'false';
    init(/** @type {HTMLElement} */ (el));
  });
  if (section.matches?.(ROOT)) {
    section.dataset.ctFsrReady = 'false';
    init(section);
  }
});
